import { useIsPgCorpUser } from '@gik/api/config/useIsPgCorpUser';
import { useLoggedInUser } from '@gik/api/users/user';
import { useUserStore } from '@gik/core/store/UserStore';
import React from 'react';
import { type CorpNavLink, corpGetStartedLink, corpHomeHref, corpLogInLink, corpNavLinks } from './corpNavItems';

/**
 * Corp (PG Corp / Howl Tools) top navigation bar — GIK-11069.
 *
 * Renders a slim black strip above the normal Give InKind header, giving a
 * PG-Corp-linked user a way back to the Corp portal. It self-gates: it renders
 * nothing unless {@link useIsPgCorpUser} is true (signed-in AND account linked
 * to PG Corp), so it costs nothing for every other visitor.
 *
 * Two layouts share this one component, toggled by CSS at the md breakpoint:
 * - Desktop: Wolfe wordmark, perfectgift-corporate wordmark, Merchant + the
 *   active Howl Tools tab, then Log In / Get Started.
 * - Mobile: the Wolfe (TWC) icon, the active Howl Tools tab centred, and a
 *   hamburger that opens a drawer with the remaining items.
 *
 * All links are external, full-page navigations to the Corp portal, so they are
 * plain `<a>` anchors rather than the in-app `Link`. Styling lives in
 * `corp-top-nav.css` (imported from the app's `index.css`); `ui-legacy` is not
 * scanned by Tailwind, so utility classes here would be purged.
 */
export function CorpTopNav(): React.ReactElement | null {
  const isPgCorpUser = useIsPgCorpUser();
  const [isMobileOpen, setIsMobileOpen] = React.useState(false);

  // Signed-in user, for the account menu on the right. `userId` is available
  // immediately from the persisted store; the name arrives with the user fetch.
  const userId = useUserStore(store => store.id);
  const user = useLoggedInUser();
  const userName = user?.fullName || [user?.firstName, user?.lastName].filter(Boolean).join(' ') || '';

  /*
   * Visibility depends entirely on client-only state (the persisted user and
   * dev stores, and the SWR `config` cache), so it cannot be resolved during
   * SSR. Render nothing until mounted so the server output and the first
   * client render agree, then reveal the bar — otherwise React reports a
   * hydration mismatch when the bar appears only on the client.
   */
  const [isMounted, setIsMounted] = React.useState(false);
  React.useEffect(() => setIsMounted(true), []);

  if (!isMounted || !isPgCorpUser) return null;

  const closeMobile = () => setIsMobileOpen(false);
  const activeLink = corpNavLinks.find(link => link.active);

  return (
    <header className="gik-corp-top-nav">
      <div className="gik-container gik-container--breakpoint-auto gik-corp-top-nav__container">
        {/* Desktop layout */}
        <div className="gik-corp-top-nav__inner gik-corp-top-nav__inner--desktop">
          <div className="gik-corp-top-nav__group">
            <a href={corpHomeHref} className="gik-corp-top-nav__brand" aria-label="Wolfe">
              <img className="gik-corp-top-nav__logo-desktop" src="/wolfe/Logo-Wolfe.svg" alt="Wolfe" />
            </a>
            <nav className="gik-corp-top-nav__nav">
              <PerfectGiftTab href={corpHomeHref} />
              {corpNavLinks.map(link => (
                <NavLink key={link.label} link={link} />
              ))}
            </nav>
          </div>

          <div className="gik-corp-top-nav__actions">
            {userId ? (
              <CorpUserMenu name={userName} />
            ) : (
              <>
                <a href={corpLogInLink.href} className="gik-corp-top-nav__login">
                  {corpLogInLink.label}
                </a>
                <a href={corpGetStartedLink.href} className="gik-corp-top-nav__cta">
                  {corpGetStartedLink.label}
                </a>
              </>
            )}
          </div>
        </div>

        {/* Mobile layout */}
        <div className="gik-corp-top-nav__inner gik-corp-top-nav__inner--mobile">
          <a href={corpHomeHref} className="gik-corp-top-nav__brand" aria-label="Wolfe">
            <img className="gik-corp-top-nav__logo-mobile" src="/wolfe/Logo-TWC.svg" alt="Wolfe" />
          </a>

          {activeLink && (
            <nav className="gik-corp-top-nav__nav gik-corp-top-nav__nav--mobile">
              <NavLink link={activeLink} />
            </nav>
          )}

          <button
            type="button"
            aria-label={isMobileOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={isMobileOpen}
            onClick={() => setIsMobileOpen(open => !open)}
            className="gik-corp-top-nav__burger"
          >
            <HamburgerIcon isOpen={isMobileOpen} />
          </button>
        </div>
      </div>

      {/* Mobile drawer + backdrop */}
      {isMobileOpen && (
        <button type="button" aria-label="Close menu" className="gik-corp-top-nav__backdrop" onClick={closeMobile} />
      )}
      {isMobileOpen && (
        <nav className="gik-corp-top-nav__drawer">
          {/* {userId && (
            <div className="gik-corp-top-nav__drawer-user">
              <span className="gik-corp-top-nav__user-icon" aria-hidden="true" />
              <span className="gik-corp-top-nav__user-name">{userName}</span>
            </div>
          )} */}
          <PerfectGiftTab href={corpHomeHref} onClick={closeMobile} />
          {corpNavLinks.map(link => (
            <NavLink key={link.label} link={link} onClick={closeMobile} />
          ))}
          {!userId && (
            <>
              <a href={corpLogInLink.href} onClick={closeMobile} className="gik-corp-top-nav__login">
                {corpLogInLink.label}
              </a>
              <a href={corpGetStartedLink.href} onClick={closeMobile} className="gik-corp-top-nav__cta">
                {corpGetStartedLink.label}
              </a>
            </>
          )}
        </nav>
      )}
    </header>
  );
}

/**
 * The signed-in account menu (desktop): user icon + name + caret, toggling a
 * dropdown. Only shown when a user is signed in — which, given the bar's
 * visibility rule, is always the case on Give InKind.
 */
function CorpUserMenu({ name }: { name: string }): React.ReactElement {
  const [isOpen, setIsOpen] = React.useState(false);
  const ref = React.useRef<HTMLDivElement>(null);

  // Close the dropdown when clicking outside it.
  React.useEffect(() => {
    if (!isOpen) return;
    const onPointerDown = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [isOpen]);

  return (
    <div className="gik-corp-top-nav__user" ref={ref}>
      <button
        type="button"
        className="gik-corp-top-nav__user-trigger"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        onClick={() => setIsOpen(open => !open)}
      >
        <span className="gik-corp-top-nav__user-icon" aria-hidden="true" />
        <span className="gik-corp-top-nav__user-name">{name}</span>
        <img
          className={
            isOpen ? 'gik-corp-top-nav__user-caret gik-corp-top-nav__user-caret--open' : 'gik-corp-top-nav__user-caret'
          }
          src="/wolfe/caret-down.svg"
          alt=""
          aria-hidden="true"
        />
      </button>

      {isOpen && (
        <div className="gik-corp-top-nav__user-menu" role="menu">
          {/* TODO(GIK-11069): confirm the account-menu items with design. */}
          <a href={corpHomeHref} className="gik-corp-top-nav__user-menu-item" role="menuitem">
            My Account
          </a>
        </div>
      )}
    </div>
  );
}

/** A primary nav link, with the active modifier (and `aria-current`) when set. */
function NavLink({ link, onClick }: { link: CorpNavLink; onClick?: () => void }): React.ReactElement {
  return (
    <a
      href={link.href}
      onClick={onClick}
      aria-current={link.active ? 'page' : undefined}
      className={link.active ? 'gik-corp-top-nav__link gik-corp-top-nav__link--active' : 'gik-corp-top-nav__link'}
    >
      {link.label}
    </a>
  );
}

/**
 * The "perfectgift.com corporate" brand mark (desktop).
 *
 * Two logo variants exist: `PGCorp.svg` (coloured) for when perfectgift is the
 * active section — shown inside the white tab — and `PGCorp-white.svg` for when
 * it is not. On Give InKind the active section is always Howl Tools, so
 * perfectgift is never active here and always uses the white variant.
 */
function PerfectGiftTab({ href, onClick }: { href: string; onClick?: () => void }): React.ReactElement {
  return (
    <a href={href} onClick={onClick} className="gik-corp-top-nav__pg" aria-label="perfectgift.com corporate">
      <img className="gik-corp-top-nav__pg-logo" src="/wolfe/PGCorp-white.svg" alt="perfectgift.com corporate" />
    </a>
  );
}

function HamburgerIcon({ isOpen }: { isOpen: boolean }): React.ReactElement {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      aria-hidden="true"
    >
      {isOpen ? (
        <>
          <line x1="5" y1="5" x2="19" y2="19" />
          <line x1="19" y1="5" x2="5" y2="19" />
        </>
      ) : (
        <>
          <line x1="3" y1="7" x2="21" y2="7" />
          <line x1="3" y1="12" x2="21" y2="12" />
          <line x1="3" y1="17" x2="21" y2="17" />
        </>
      )}
    </svg>
  );
}
